import { ApiError } from '@echo/api-client/client';
import { groupProgress } from '@echo/api-client/echo';
import { useQueries } from '@tanstack/react-query';
import { groupDashboardDetails, type SubGroupProgress } from '../content/member-progress';
import {
  createMockGroupDashboardDetails,
  type GroupDashboardDetailsStatus,
  type GroupDashboardSource,
  resolveGroupDashboard,
} from '../domain/group-dashboard';
import { queryKeys } from '../query/keys';
import { useConfig, useGroupDashboardSource, useSession } from './provider';
import { useMe } from './use-me';
import { useMyGroups } from './use-my-groups';
import { todayIso } from './use-today';

/** Answers from echo-api that mean the statistics aren't there for this person, not a failure. */
const REFUSALS: readonly number[] = [401, 403, 404];

/** The statistics from echo's backend: the week the Groups card reads, member by member. */
const echoDashboardSource: GroupDashboardSource = {
  getDetails: async (groupId) =>
    groupDashboardDetails(
      (await groupProgress(groupId, { today: todayIso() })).data as SubGroupProgress,
    ),
};

/** Live memberships enriched with optional details; an empty membership list stays empty. */
export function useGroupDirectory(enabled = true) {
  const groups = useMyGroups(enabled);
  const config = useConfig();
  const injected = useGroupDashboardSource();
  // An injected source wins; then the local preview, when asked for; then echo's backend.
  const preview = !injected && config.environment === 'local' && config.mockGroups === true;
  const source =
    injected ?? (!preview && config.echoApiUrl !== undefined ? echoDashboardSource : undefined);
  const session = useSession();
  const me = useMe(enabled && !!source);
  const viewerId = me.data?.uuid;
  const details = useQueries({
    queries: (enabled && source ? (groups.data ?? []) : []).map((group) => ({
      queryKey: queryKeys.groupDashboard(viewerId, group.actorUuid),
      enabled: session.status === 'authenticated' && viewerId !== undefined,
      staleTime: 30_000,
      // A refusal stays a refusal (a group that's gone, members Core hides): no retries.
      retry: (failures: number, error: Error) =>
        !(error instanceof ApiError && error.status < 500) && failures < 2,
      queryFn: async () => (await source?.getDetails(group.actorUuid)) ?? null,
    })),
  });
  const needsIdentity = enabled && !!source && (groups.data?.length ?? 0) > 0;
  const data = (enabled && session.status === 'authenticated' ? groups.data : undefined)?.map(
    (group, index) => {
      const query = details[index];
      const liveDetails = me.isError || query?.isError ? undefined : (query?.data ?? undefined);
      // echo-api saying no (no sign-in it accepts, members Core hides, a group not under
      // Stronger Memory) won't change on a retry: the card falls back to the group's day.
      const refused = query?.error instanceof ApiError && REFUSALS.includes(query.error.status);
      const detailsStatus: GroupDashboardDetailsStatus = preview
        ? 'available'
        : !source || refused
          ? 'unavailable'
          : me.isError || query?.isError
            ? 'error'
            : me.isPending || (viewerId !== undefined && query?.isPending)
              ? 'loading'
              : liveDetails
                ? 'available'
                : 'unavailable';
      return resolveGroupDashboard(
        group,
        preview
          ? {
              ...createMockGroupDashboardDetails(index),
              role: group.manages ? 'facilitator' : 'member',
            }
          : liveDetails,
        preview,
        detailsStatus,
      );
    },
  );
  const retryDetails = async (groupActorUuid?: string) => {
    if (!enabled || !source || session.status !== 'authenticated') return;
    if (needsIdentity && (viewerId === undefined || me.isError)) {
      await me.refetch();
      // The next render enables the queries under the resolved viewer's cache key.
      if (viewerId === undefined) return;
    }
    await Promise.all(
      details
        .filter((_, index) =>
          groupActorUuid === undefined ? true : groups.data?.[index]?.actorUuid === groupActorUuid,
        )
        .map((query) => query.refetch()),
    );
  };
  return {
    data,
    // Optional dashboard statistics must never block live membership or its actions.
    isPending: enabled && groups.isPending,
    isError: enabled && groups.isError,
    error: enabled ? groups.error : null,
    isSuccess: enabled && groups.isSuccess,
    retryDetails,
    refetch: async () => {
      if (!enabled) return;
      await groups.refetch();
      await retryDetails();
    },
  };
}

export function useGroupDashboard(groupActorUuid: string | undefined) {
  const directory = useGroupDirectory();
  return {
    ...directory,
    group: directory.data?.find((group) => group.actorUuid === groupActorUuid),
    refetchDetails: () => directory.retryDetails(groupActorUuid),
  };
}
